import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { AllocationDriftCard } from '../components/strategy/AllocationDriftCard'
import { useThesisAllocations, useThesisHealthScores } from '../hooks/useThesis'
import type { DriftStatus, ThesisHealthScore } from '../types/thesis'
import { DRIFT_STATUS_COLORS, DRIFT_STATUS_LABELS } from '../types/thesis'
import { usePortfolioFilter } from '../contexts'

export function ThesisAllocationsPage() {
  const { selectedPortfolioId } = usePortfolioFilter()
  const { data, isLoading, error } = useThesisAllocations(selectedPortfolioId)
  const { data: healthData, isLoading: healthLoading } = useThesisHealthScores(selectedPortfolioId)
  const [statusFilter, setStatusFilter] = useState<DriftStatus | ''>('')

  // Create lookup map for health scores by thesis_id
  const healthScoresMap = useMemo(() => {
    const map = new Map<string, ThesisHealthScore>()
    healthData?.scores.forEach((score) => {
      map.set(score.thesis_id, score)
    })
    return map
  }, [healthData])

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  if (isLoading || healthLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        Failed to load thesis allocations: {error.message}
      </div>
    )
  }

  const allocations = data?.allocations || []
  const filteredAllocations = statusFilter
    ? allocations.filter((a) => a.drift_status === statusFilter)
    : allocations

  // Count by status
  const statusCounts = {
    ok: allocations.filter((a) => a.drift_status === 'ok').length,
    warning: allocations.filter((a) => a.drift_status === 'warning').length,
    alert: allocations.filter((a) => a.drift_status === 'alert').length,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Thesis Allocations</h1>
          <p className="text-gray-500 mt-1">
            Monitor your portfolio allocation against thesis targets
          </p>
        </div>
        {/* Contextual rebalance action - show when there's drift */}
        {(statusCounts.warning > 0 || statusCounts.alert > 0) && (
          <Link
            to="/what-if/rebalance"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Rebalance Preview
          </Link>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Portfolio Value</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(data?.total_portfolio_value || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Allocated to Theses</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(data?.total_allocated_value || 0)}
          </p>
          <p className="text-sm text-gray-400">
            {data?.total_portfolio_value && data.total_portfolio_value > 0
              ? `${(100 - (data.unallocated_pct || 0)).toFixed(1)}% of portfolio`
              : '0%'}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Unallocated</p>
          <p className="text-2xl font-bold text-orange-600">
            {(data?.unallocated_pct || 0).toFixed(1)}%
          </p>
          <p className="text-sm text-gray-400">
            {formatCurrency((data?.total_portfolio_value || 0) - (data?.total_allocated_value || 0))}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Average Health</p>
          <p className={`text-2xl font-bold ${
            (healthData?.average_health ?? 0) >= 80 ? 'text-green-600' :
            (healthData?.average_health ?? 0) >= 60 ? 'text-blue-600' :
            (healthData?.average_health ?? 0) >= 40 ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {(healthData?.average_health ?? 0).toFixed(0)}/100
          </p>
          <p className="text-sm text-gray-400">
            across {healthData?.scores.length || 0} theses
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active Theses</p>
          <p className="text-2xl font-bold text-gray-900">{allocations.length}</p>
          <div className="flex gap-2 mt-1">
            <span className="text-xs text-green-600">{statusCounts.ok} on target</span>
            <span className="text-xs text-yellow-600">{statusCounts.warning} drifting</span>
            <span className="text-xs text-red-600">{statusCounts.alert} off</span>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Filter:</span>
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1 text-sm rounded-full transition-colors ${
            statusFilter === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({allocations.length})
        </button>
        {(['ok', 'warning', 'alert'] as DriftStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status === statusFilter ? '' : status)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              statusFilter === status
                ? DRIFT_STATUS_COLORS[status].replace('100', '600').replace('800', 'white')
                : `${DRIFT_STATUS_COLORS[status]} hover:opacity-80`
            }`}
          >
            {DRIFT_STATUS_LABELS[status]} ({statusCounts[status]})
          </button>
        ))}
      </div>

      {/* Allocations Grid */}
      {filteredAllocations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          {allocations.length === 0 ? (
            <>
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No thesis allocations</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create theses and link holdings to see allocation data.
              </p>
            </>
          ) : (
            <p className="text-gray-500">No theses match the selected filter.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAllocations.map((allocation) => (
            <AllocationDriftCard
              key={allocation.thesis_id}
              allocation={allocation}
              healthScore={healthScoresMap.get(allocation.thesis_id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
